// @flow
import update from 'immutability-helper';
import { of } from 'rxjs';
import { ofType, StateObservable } from 'redux-observable';
import { catchError, filter, switchMap, map, takeUntil, mergeMap, delay } from 'rxjs/operators';
import { OrderedMap } from 'immutable';
import {
  getNotificationsApi, getUnseenApi, seeAllNotificationApi, hideNotificationApi, readNotificationApi, endpoints
} from './api';
import { logout } from '../../auth/duck';
import Paginator from '../../../common/Paginator';
import {
  notificationsCustomIncrement,
  notificationsCustomSeeAllOperation,
  notificationsCustomReadOperation,
  notificationsCustomHideOperation,
  countSingleUnseen, notificationsCustomPrependOperation,
} from './utils';

import type { Observable } from 'rxjs';
import type { Notification, User } from '../../../entities';
import type { HTTPError } from '../../../error';
import type { Store } from 'redux';
import type { RootState } from '../../index';

export type NotificationsAction = {
  +type: string;
  +notifications?: Notification[];
  +error?: HTTPError;
  +unseen?: number;
  +id?: string;
  // new notification realtime
  +notification?: Notification;
};

export type NotificationsState = {
  notifications?: Notification[];
  isFetching?: boolean;
  page?: number;
  pageCanAsk?: boolean;
  fetchError?: HTTPError;
  isFetchingUnseen?: boolean;
  unseen?: number;
  // No need to track unseen error yet
};

// Actions
// action what do nothing
const NOTIFICATIONS_NOOP = 'NOTIFICATIONS_NOOP';
const NOTIFICATIONS_RESET_STATE = 'NOTIFICATIONS_RESET_STATE';
const NOTIFICATIONS_FETCH_REQUEST = 'NOTIFICATIONS_FETCH_REQUEST';
const NOTIFICATIONS_FETCH_SUCCESS = 'NOTIFICATIONS_FETCH_SUCCESS';
const NOTIFICATIONS_FETCH_ERROR = 'NOTIFICATIONS_FETCH_ERROR';
const NOTIFICATIONS_FETCH_UNSEEN_REQUEST = 'NOTIFICATIONS_FETCH_UNSEEN_REQUEST';
const NOTIFICATIONS_FETCH_UNSEEN_SUCCESS = 'NOTIFICATIONS_FETCH_UNSEEN_SUCCESS';
// we don't care for success or fail of seen
const NOTIFICATIONS_SEE_ALL_REQUEST = 'NOTIFICATIONS_SEE_ALL_REQUEST';
const NOTIFICATIONS_HIDE_REQUEST = 'NOTIFICATIONS_HIDE_REQUEST';
const NOTIFICATIONS_READ_REQUEST = 'NOTIFICATIONS_READ_REQUEST';
// websocket new notif arrival
const NOTIFICATIONS_NEW_REALTIME = 'NOTIFICATIONS_NEW_REALTIME';

// Action Creators
function noopAction(): NotificationsAction {
  return {
    type: NOTIFICATIONS_NOOP,
  }
}

export function reset(): NotificationsAction {
  return {
    type: NOTIFICATIONS_RESET_STATE,
  };
}

export function fetchNotifications(): NotificationsAction {
  return {
    type: NOTIFICATIONS_FETCH_REQUEST,
  };
}

export function fetchNotificationsSuccess(notifications: Notification[]): NotificationsAction {
  return {
    type: NOTIFICATIONS_FETCH_SUCCESS,
    notifications
  };
}

export function fetchNotificationsError(error: HTTPError): NotificationsAction {
  return {
    type: NOTIFICATIONS_FETCH_ERROR,
    error,
  }
}

export function fetchUnseen(): NotificationsAction {
  return {
    type: NOTIFICATIONS_FETCH_UNSEEN_REQUEST,
  };
}

export function fetchUnseenSuccess(unseen: number): NotificationsAction {
  return {
    type: NOTIFICATIONS_FETCH_UNSEEN_SUCCESS,
    unseen,
  };
}

export function seeAllNotification(): NotificationsAction {
  return {
    type: NOTIFICATIONS_SEE_ALL_REQUEST,
  };
}

export function readNotification(id: string): NotificationsAction {
  return {
    type: NOTIFICATIONS_READ_REQUEST,
    id,
  };
}

export function hideNotification(id: string): NotificationsAction {
  return {
    type: NOTIFICATIONS_HIDE_REQUEST,
    id,
  };
}

export function newRealtimeNotification(notification: Notification): NotificationsAction {
  return {
    type: NOTIFICATIONS_NEW_REALTIME,
    notification,
  }
}

export const actions = {
  NOTIFICATIONS_NOOP,
  NOTIFICATIONS_RESET_STATE,
  NOTIFICATIONS_FETCH_REQUEST,
  NOTIFICATIONS_FETCH_SUCCESS,
  NOTIFICATIONS_FETCH_ERROR,
  NOTIFICATIONS_FETCH_UNSEEN_REQUEST,
  NOTIFICATIONS_FETCH_UNSEEN_SUCCESS,
  NOTIFICATIONS_SEE_ALL_REQUEST,
  NOTIFICATIONS_READ_REQUEST,
  NOTIFICATIONS_HIDE_REQUEST,
  NOTIFICATIONS_NEW_REALTIME,
  noopAction,
  reset,
  fetchNotifications,
  fetchNotificationsSuccess,
  fetchNotificationsError,
  fetchUnseen,
  fetchUnseenSuccess,
  seeAllNotification,
  readNotification,
  hideNotification,
  newRealtimeNotification,
};

export const initialState = {};

const paginator = new Paginator(['notifications']);

// Reducer
export default function reducer(state: NotificationsState = initialState,
                                action: NotificationsAction) {
  switch (action.type) {
    case NOTIFICATIONS_FETCH_REQUEST:
      return update(state, {
        isFetching: { $set: true },
      });
    case NOTIFICATIONS_FETCH_SUCCESS:
      paginator.incrementCustom('notifications', action.notifications,
        state.notifications, notificationsCustomIncrement);
      return update(state, {
        $unset: ['isFetching'],
        notifications: { $set: paginator.getItems('notifications') },
        page: { $set: paginator.getPage('notifications') },
        pageCanAsk: { $set: paginator.getCanAsk('notifications') },
      });
    case NOTIFICATIONS_FETCH_ERROR:
      return update(state, {
        $unset: ['isFetching'],
        fetchError: { $set: action.error },
      });
    case NOTIFICATIONS_FETCH_UNSEEN_REQUEST:
      return update(state, {
        isFetchingUnseen: { $set: true },
      });
    case NOTIFICATIONS_FETCH_UNSEEN_SUCCESS:
      return update(state, {
        $unset: ['isFetchingUnseen'],
        unseen: { $set: action.unseen },
      });
    case NOTIFICATIONS_SEE_ALL_REQUEST:
      paginator.executeCustomOperation('notifications',
        (map: OrderedMap<string, Notification>) => notificationsCustomSeeAllOperation(map));
      return update(state, {
        notifications: { $set: paginator.getItems('notifications') },
        unseen: { $set: 0 },
      });
    case NOTIFICATIONS_READ_REQUEST:
      let unseenCountRead = 0;
      const readCache = paginator.getRawCache('notifications');
      if (readCache) unseenCountRead = countSingleUnseen(readCache, action);
      paginator.executeCustomOperation('notifications',
        (map: OrderedMap<string, Notification>) => notificationsCustomReadOperation(map, action));
      return update(state, {
        notifications: { $set: paginator.getItems('notifications') },
        unseen: { $set: Math.max((state.unseen || 0) - unseenCountRead, 0) },
      });
    case NOTIFICATIONS_HIDE_REQUEST:
      let unseenCountHide = 0;
      const hideCache = paginator.getRawCache('notifications');
      if (hideCache) unseenCountHide = countSingleUnseen(hideCache, action);
      // TODO: handle delete notifications inside a group
      paginator.executeCustomOperation('notifications',
        (map: OrderedMap<string, Notification>) => notificationsCustomHideOperation(map, action));
      return update(state, {
        notifications: { $set: paginator.getItems('notifications') },
        unseen: { $set: Math.max((state.unseen || 0) - unseenCountHide, 0) },
      });
    case NOTIFICATIONS_NEW_REALTIME:
      paginator.executeCustomOperation('notifications',
        (map: OrderedMap<string, Notification>) => notificationsCustomPrependOperation(map, action.notification));
      return update(state, {
        notifications: { $set: paginator.getItems('notifications') },
        unseen: { $set: state.unseen + 1 },
      });
    case NOTIFICATIONS_RESET_STATE:
      paginator.resetAll();
      return initialState;
    default:
      return state;
  }
}

// Epics
export function seeAllNotificationEpic(action$: Observable<NotificationsAction>): Observable<NotificationsAction> {
  return action$.pipe(
    ofType(NOTIFICATIONS_SEE_ALL_REQUEST),
    mergeMap(() => seeAllNotificationApi().pipe(
      map(() => noopAction()))
    ));
}

export function readNotificationEpic(action$: Observable<NotificationsAction>): Observable<NotificationsAction> {
  return action$.pipe(
    ofType(NOTIFICATIONS_READ_REQUEST),
    mergeMap(({ id }) => readNotificationApi(undefined, {
        route: endpoints.READ_NOTIFICATION.replace(':id', id),
      }).pipe(
      map(() => noopAction()))
    ));
}

export function fetchNotificationsSuccessEpic(action$: Observable<NotificationsAction>, store: Store<RootState>): Observable<NotificationsAction> {
  return action$.pipe(
    ofType(NOTIFICATIONS_FETCH_SUCCESS),
    // When we are reducing notifications, we also track the number of notifications remaining
    // to ask for more when is reaching the end. End-offset: 20 (different from hide)
    filter(() => store.value.navbar.notifications.pageCanAsk),
    filter(() => store.value.navbar.notifications.notifications.length < 20),
    map(() => fetchNotifications())
  );
}

export function fetchNotificationsEpic(action$: Observable<NotificationsAction>, state$: StateObservable<RootState>): Observable<NotificationsAction> {
  return action$.pipe(
    ofType(NOTIFICATIONS_FETCH_REQUEST),
    switchMap(() => getNotificationsApi({
        page: state$.value.navbar.notifications.page || paginator.getPage('notifications'),
        limit: paginator.getLimit(),
      }).pipe(
      takeUntil(action$.pipe(
        ofType(NOTIFICATIONS_FETCH_REQUEST))),
      map((response: Notification[]) => fetchNotificationsSuccess(response)),
      catchError((e: HTTPError) => of(fetchNotificationsError(e))))
    ));
}

// TODO: handle delete notifications inside a group
export function hideNotificationEpic(action$: Observable<NotificationsAction>, store: Store<RootState>): Observable<NotificationsAction> {
  // We use just map cos we want the stream to flow (with mergeMap/switchMap it gets stuck)
  return action$.pipe(
    ofType(NOTIFICATIONS_HIDE_REQUEST),
    map(({ id }) => hideNotificationApi(undefined, {
      route: endpoints.HIDE_NOTIFICATION.replace(':id', id),
    })),
    // When we are deleting, we also track the number of notifications remaining
    // to ask for more when is reaching the end. End-offset: 10
    filter(() => store.value.navbar.notifications.pageCanAsk),
    filter(() => store.value.navbar.notifications.notifications.length < 10),
    map(() => fetchNotifications())
  );
}

export function fetchUnseenEpic(action$: Observable<NotificationsAction>, state$: StateObservable): Observable<NotificationsAction> {
  return action$.pipe(
    ofType(NOTIFICATIONS_FETCH_UNSEEN_REQUEST),
    switchMap(() => getUnseenApi().pipe(
      map(({ count }: { count: number }) => fetchUnseenSuccess(count)),
      catchError((e: HTTPError) => {
        const state: RootState = state$.value;
        const user: User = state.auth.user;
        // Since is the emptier epic, and it executes once at start of session
        // I chose it to be the responsible for logout if not
        if (e.code === 403 && user) return of(logout(user.id));
        return of(fetchNotificationsError(e));
      }))
    ));
}



// WEBPACK FOOTER //
// ./src/modules/navbar/notifications/duck.js